import { useQuery } from "react-query";
import { type FC, MouseEvent, useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { fetchSortTypes } from "@/api/catalogAPI";
import { fetchFavorites } from "@/api/favoritesAPI";
import { fetchProductsList } from "@/api/productsAPI";
import { ActionBar } from "@/components/ActionBar/ActionBar";
import EmptyEntries from "@/components/EmptyEntries";
import { StyledTileProducts } from "@/components/Products";
import { ProductsGrid } from "@/components/Products/Grid";
import { LazyHydrateContainer } from "@/hoc/LazyHydrate";
import { useAuth } from "@/hooks/auth/auth";
import { getDefaultFromGroups, normalizeSearchParamsFavorites, useFavorites } from "@/hooks/favorites";
import { useModals } from "@/hooks/modals/useModals";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { modalsAlias } from "@/store/reducers/commonSlice";
import { setFavorites, setSortBy } from "@/store/reducers/favoritesSlice";
import { cssGap15 } from "@/styles/utils/Utils";
import { colors } from "@/styles/utils/vars";
import { Button } from "@/ui/Button/Button";
import { ButtonGroup } from "@/ui/Button/StyledButton";
import { BaseLoader } from "@/ui/Loaders/BaseLoader/BaseLoader";
import { Modal } from "@/ui/Modal";
import { Typography } from "@/ui/Typography/Typography";
import { VIEW_PRODUCTS_GRID } from "@/utils/constants";
import { sortProductsFromApi } from "@/utils/common/helpers";
import { ClearListButton } from "./ClearListButton";
import { CreateListButton } from "./CreateListButton";
import { FiltersPage } from "./FiltersPage";
import { GroupsTags } from "./GroupsTags";
import { ShareListButton } from "./ShareListButton";
import { StyledFavorites, StyledGroupsTagsWrapper } from "./Styled";
const FavoritesContent: FC = () => {
  const router = useRouter();
  const {
    keys,
    groupsProductsIds,
    groups,
    filter: {
      selectedGroup
    }
  } = useAppSelector(({
    favorites
  }) => favorites);
  const dispatch = useAppDispatch();
  const [{
    isShow: isShowGroupDeleteModal,
    groupId: deleteGroupId
  }, setDeleteGroupProps] = useState<{
    isShow: boolean;
    groupId: string | null;
  }>({
    isShow: false,
    groupId: null
  });
  const {
    show: showModal
  } = useModals();
  const {
    isAuth,
    isInit: isInitAuth
  } = useAuth();
  const {
    removeGroup
  } = useFavorites();
  const requestOptions = normalizeSearchParamsFavorites({
    query: router.query
  });
  useQuery(["sortTypes"], () => fetchSortTypes(), {
    onSuccess: response => {
      dispatch(setSortBy(response || []));
    },
    cacheTime: Infinity
  });
  useQuery(["favorites", requestOptions, isInitAuth, isAuth], () => fetchFavorites(requestOptions), {
    enabled: isInitAuth && isAuth && !!requestOptions?.group,
    onSuccess: response => {
      if (!response) {
        return;
      }
      dispatch(setFavorites({
        requestOptions: requestOptions,
        data: response
      }));
    }
  });
  const keysProducts = useMemo(() => {
    if (!isInitAuth) {
      return [];
    }
    if (isAuth) {
      return !!selectedGroup ? groupsProductsIds[selectedGroup] || [] : [];
    }
    return keys || [];
  }, [groupsProductsIds, isAuth, isInitAuth, keys, selectedGroup]);
  const {
    data: productsData,
    isFetching: isFetchingFavoritesProducts
  } = useQuery(["favoritesProducts", keysProducts], () => fetchProductsList({
    uuids: keysProducts.join(",")
  }), {
    enabled: keysProducts.length > 0
  });
  const products = useMemo(() => sortProductsFromApi(productsData || [], keysProducts), [keysProducts, productsData]);
  const isFetchingProducts = isFetchingFavoritesProducts;
  const hasProducts = products.length > 0;
  const changeGroupHandle = useCallback((id: string | number | null) => {
    const url = new URL(window.location.href);
    url.searchParams.delete("page");
    if (!!id) {
      url.searchParams.set("group", String(id));
    } else {
      url.searchParams.delete("group");
    }
    void router.push(url.toString(), undefined, {
      shallow: true,
      scroll: true
    });
  }, []);
  const deleteGroupHandle = useCallback((id: string | number) => {
    setDeleteGroupProps({
      isShow: true,
      groupId: `${id}`
    });
  }, []);
  const approveDeleteGroupHandle = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (deleteGroupId) {
      removeGroup(deleteGroupId);
    }
    setDeleteGroupProps({
      isShow: false,
      groupId: null
    });
    if (deleteGroupId === selectedGroup) {
      const defaultGroup = getDefaultFromGroups(groups);
      changeGroupHandle(!!defaultGroup ? defaultGroup.uid : null);
    }
  }, [deleteGroupId, groups, removeGroup, changeGroupHandle, selectedGroup]);
  const cancelDeleteGroup = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDeleteGroupProps({
      isShow: false,
      groupId: null
    });
  }, []);
  useLayoutEffect(() => {
    // если выбрана группа, для которой нужно показывать товары, ничего не делаем
    if (!!requestOptions.group) {
      return;
    }
    if (!groups) {
      return;
    }

    // если группы не выбрано, предполагаем, зашли на страницу впервые
    // активируем группу товаров по умолчанию
    const defaultGroup = getDefaultFromGroups(groups);
    if (!defaultGroup) {
      return;
    }
    changeGroupHandle(defaultGroup.uid);
  }, [changeGroupHandle, groups, requestOptions.group]);
  return <StyledFavorites>
      {isFetchingProducts && <BaseLoader isFixed />}

      {!!keys && keys.length > 0 && !isAuth && <ActionBar variant={"red"} icon={"Warning"} iconSize={"default"} description={<Typography style={{
      color: colors.black
    }}>
              <Button variant={"link"} as={"a"} onClick={() => {
        showModal({
          alias: modalsAlias.auth
        });
      }}>
                Авторизуйтесь
              </Button>
              , чтобы сохранить список избранного для просмотра на этом и других
              устройствах или в мобильном приложении.
            </Typography>} />}

      {isAuth && <ButtonGroup className={cssGap15}>
          <CreateListButton />
          <ClearListButton />
          {!!selectedGroup && hasProducts && <ShareListButton categoryUid={selectedGroup} />}
        </ButtonGroup>}

      <LazyHydrateContainer>
        <StyledGroupsTagsWrapper>
          <GroupsTags selectedGroup={selectedGroup} onSelectGroup={changeGroupHandle} onDeleteGroup={deleteGroupHandle} />
        </StyledGroupsTagsWrapper>
      </LazyHydrateContainer>

      <Modal isShowModal={isShowGroupDeleteModal} title="Удалить группу?" closeMode="destroy" onClose={() => {
      setDeleteGroupProps({
        isShow: false,
        groupId: null
      });
    }}>
        <LazyHydrateContainer>
          <ButtonGroup>
            <Button type={"button"} variant={"link"} size={"large"} onClick={approveDeleteGroupHandle}>
              Да, удалить
            </Button>
            <Button type={"button"} variant={"filled"} size={"large"} onClick={cancelDeleteGroup}>
              Нет, оставить
            </Button>
          </ButtonGroup>
        </LazyHydrateContainer>
      </Modal>

      {isAuth && hasProducts && <LazyHydrateContainer>
          <FiltersPage />
        </LazyHydrateContainer>}

      <LazyHydrateContainer>
        {hasProducts ? <ProductsGrid as={StyledTileProducts} products={products} view={VIEW_PRODUCTS_GRID} favoritesGroupId={selectedGroup} /> : <EmptyEntries />}
      </LazyHydrateContainer>
    </StyledFavorites>;
};
export const Favorites = () => {
  const {
    groups
  } = useAppSelector(({
    favorites
  }) => favorites);
  if (!groups) {
    return null;
  }
  return <FavoritesContent />;
};